import { EMPTY, Subscription, timer } from 'rxjs'
import { catchError, filter, map, switchMap, take } from 'rxjs/operators'

import { Injectable, OnDestroy } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { environment } from 'src/environments/environment'

import { AuthenticationService } from './authentication.service'
import { EndpointOptions, RequestService } from './request.service'

type StartSessionResponse = {
    session_id: string
    started_at: string
}

type AnalyticsEvent = {
    eventName: string
    route?: string
    entityType?: string
    entityId?: string
    durationMs?: number
    properties?: Record<string, unknown>
}

@Injectable({
    providedIn: 'root'
})
export class ProductAnalyticsService implements OnDestroy {
    private readonly sessionStorageKey = 'kartoriumProductAnalyticsSessionId'
    private readonly subscriptions = new Subscription()
    private readonly ignoredRoutes = new Set(['', '/', '/login', '/signup'])
    private initialized = false
    private heartbeatStarted = false
    private sessionId: string | null = null
    private sessionStarting = false
    private authToken: string | null = null
    private queuedEvents: AnalyticsEvent[] = []
    private lastRoute = ''

    constructor(
        private authenticationService: AuthenticationService,
        private requestService: RequestService,
        private router: Router,
    ) {}

    initialize(): void {
        if (this.initialized) return
        this.initialized = true

        this.subscriptions.add(
            this.authenticationService.isAuthenticated$.pipe(
                filter(Boolean),
                take(1),
            ).subscribe(() => {
                this.cacheAuthToken()
                this.startHeartbeat()
                this.startRouteTracking()
                this.startVisibilityTracking()
                this.trackPageView(this.router.url)
            })
        )
    }

    ngOnDestroy(): void {
        this.endSession()
        this.subscriptions.unsubscribe()
    }

    track(event: AnalyticsEvent): void {
        if (this.shouldIgnoreRoute(event.route ?? this.router.url)) return

        if (!this.sessionId) {
            this.queuedEvents.push(event)
            this.startOrResumeSession(event.route ?? this.router.url)
            return
        }

        const url = `${environment.api}/analytics/events`
        const options = { error: { operation: 'Record Product Analytics Event', log: false } } as EndpointOptions
        this.requestService.create(url, {
            sessionId: this.sessionId,
            eventName: event.eventName,
            route: event.route ?? this.router.url,
            entityType: event.entityType,
            entityId: event.entityId,
            durationMs: event.durationMs,
            properties: event.properties ?? {},
        }, options).pipe(
            catchError(() => EMPTY)
        ).subscribe()
    }

    private startOrResumeSession(route: string): void {
        if (this.sessionId || this.sessionStarting) return

        const storedSessionId = sessionStorage.getItem(this.sessionStorageKey)
        if (!storedSessionId) {
            this.startSession(route)
            return
        }

        this.sessionId = storedSessionId
        this.sessionStarting = true
        let resumed = false

        this.heartbeat(route).subscribe({
            next: () => {
                resumed = true
                this.flushQueuedEvents()
            },
            complete: () => {
                this.sessionStarting = false
                if (!resumed) this.startSession(route)
            },
            error: () => {
                this.sessionStarting = false
                this.startSession(route)
            },
        })
    }

    private startSession(route: string): void {
        if (this.sessionStarting) return
        this.sessionStarting = true

        const url = `${environment.api}/analytics/sessions/start`
        const options = { error: { operation: 'Start Product Analytics Session', log: false } } as EndpointOptions

        this.requestService.create<StartSessionResponse>(url, {
            route,
            userAgent: navigator.userAgent,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            environment: environment.production ? 'production' : 'local',
            appVersion: 'web',
        }, options).pipe(
            map(session => {
                this.sessionId = session.session_id
                sessionStorage.setItem(this.sessionStorageKey, session.session_id)
                return session
            }),
            catchError(() => EMPTY),
        ).subscribe({
            next: () => this.flushQueuedEvents(),
            complete: () => { this.sessionStarting = false },
            error: () => { this.sessionStarting = false },
        })
    }

    private startHeartbeat(): void {
        if (this.heartbeatStarted) return
        this.heartbeatStarted = true

        this.subscriptions.add(
            timer(60_000, 60_000).pipe(
                switchMap(() => this.heartbeat(this.router.url)),
            ).subscribe()
        )
    }

    private heartbeat(route: string) {
        if (!this.sessionId) return EMPTY

        const url = `${environment.api}/analytics/sessions/${this.sessionId}/heartbeat`
        const options = { error: { operation: 'Heartbeat Product Analytics Session', log: false } } as EndpointOptions
        return this.requestService.create(url, { route }, options).pipe(
            catchError(() => {
                sessionStorage.removeItem(this.sessionStorageKey)
                this.sessionId = null
                return EMPTY
            })
        )
    }

    private endSession(): void {
        if (!this.sessionId) return

        const url = `${environment.api}/analytics/sessions/${this.sessionId}/end`
        const options = { error: { operation: 'End Product Analytics Session', log: false } } as EndpointOptions
        this.requestService.create(url, { route: this.router.url }, options).pipe(
            catchError(() => EMPTY)
        ).subscribe()
    }

    private endSessionWithKeepalive(): void {
        if (!this.sessionId) return

        const token = this.authToken ?? this.getAuth0AccessToken()
        const sessionId = this.sessionId
        sessionStorage.removeItem(this.sessionStorageKey)
        this.sessionId = null

        if (!token) return

        fetch(`${environment.api}/analytics/sessions/${sessionId}/end`, {
            method: 'POST',
            headers: {
                'authorization': `Bearer ${token}`,
                'content-type': 'application/json',
            },
            body: JSON.stringify({ route: this.router.url }),
            keepalive: true,
        }).catch(() => {})
    }

    private startRouteTracking(): void {
        this.subscriptions.add(
            this.router.events.pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            ).subscribe(event => this.trackPageView(event.urlAfterRedirects))
        )
    }

    private trackPageView(route: string): void {
        if (this.shouldIgnoreRoute(route)) return
        if (!route || route === this.lastRoute) return
        this.lastRoute = route
        this.track({
            eventName: 'page_view',
            route,
            properties: {
                module: this.authenticationService.currentModule,
            }
        })
    }

    private startVisibilityTracking(): void {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.heartbeat(this.router.url).subscribe()
            }
        })
        window.addEventListener('pagehide', () => this.endSessionWithKeepalive())
    }

    private flushQueuedEvents(): void {
        const queuedEvents = [...this.queuedEvents]
        this.queuedEvents = []
        queuedEvents.forEach(event => this.track(event))
    }

    private shouldIgnoreRoute(route: string | undefined): boolean {
        if (!route) return true
        const path = route.split('?')[0].split('#')[0]
        return this.ignoredRoutes.has(path)
    }

    private cacheAuthToken(): void {
        this.authenticationService.getAuthenticatedToken().pipe(
            take(1),
            catchError(() => EMPTY),
        ).subscribe(token => this.authToken = token)
    }

    private getAuth0AccessToken(): string | null {
        for (let index = 0; index < localStorage.length; index++) {
            const key = localStorage.key(index)
            if (!key || !key.toLowerCase().includes('auth0')) continue

            const token = this.findAccessToken(localStorage.getItem(key))
            if (token) return token
        }

        return null
    }

    private findAccessToken(value: unknown): string | null {
        if (!value) return null

        if (typeof value === 'string') {
            try {
                return this.findAccessToken(JSON.parse(value))
            } catch {
                return null
            }
        }

        if (typeof value !== 'object') return null

        const record = value as Record<string, unknown>
        if (typeof record['access_token'] === 'string') return record['access_token'] as string

        for (const child of Object.values(record)) {
            const token = this.findAccessToken(child)
            if (token) return token
        }

        return null
    }
}
